import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';

import { store } from 'store';

import * as S from './SophiePhaseDebug.styles';

const PHASE_LABELS: Record<string, string> = {
  listening: '🎤 USER SPEAKING — Sophie is listening',
  thinking: '⏳ THINKING — Sophie is preparing response...',
  speaking: '🔊 SOPHIE SPEAKING',
};

interface PhaseLogEntry {
  id: number;
  text: string;
  ts: number;
}

let logIdCounter = 0;

export const SophiePhaseDebug: FC = observer(() => {
  const { voiceState, sophiePhase } = store.assistantStore;
  const [phaseLog, setPhaseLog] = useState<PhaseLogEntry[]>([]);

  useEffect(() => {
    const dispose = reaction(
      () => store.assistantStore.sophiePhase,
      (phase) => {
        if (!phase) return;
        const now = Date.now();
        const label = PHASE_LABELS[phase] || phase;
        const entry: PhaseLogEntry = { id: ++logIdCounter, text: label, ts: now };
        setPhaseLog((prev) => [entry, ...prev].slice(0, 8));
      }
    );
    return dispose;
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const cutoff = Date.now() - 10_000;
      setPhaseLog((prev) => prev.filter((e) => e.ts > cutoff));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Clear log when voice stops
  useEffect(() => {
    if (voiceState !== 'active') setPhaseLog([]);
  }, [voiceState]);

  if (voiceState !== 'active' || !sophiePhase) return null;

  return (
    <>
      <S.PhaseDebugBadge $phase={sophiePhase}>
        {PHASE_LABELS[sophiePhase] || sophiePhase}
      </S.PhaseDebugBadge>
      {phaseLog.length > 0 && (
        <S.PhaseDebugLog>
          {phaseLog.map((entry) => {
            const age = (Date.now() - entry.ts) / 1000;
            const opacity = Math.max(0.2, 1 - age / 10);
            return (
              <S.PhaseDebugEntry key={entry.id} $opacity={opacity}>
                {new Date(entry.ts).toLocaleTimeString()}: {entry.text}
              </S.PhaseDebugEntry>
            );
          })}
        </S.PhaseDebugLog>
      )}
    </>
  );
});
